import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, addDays, addMonths, addYears } from "date-fns";
import { Clock, CalendarClock } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UserSubscription } from "@/types/subscription";
import { Checkbox } from "@/components/ui/checkbox";

const extensionPeriods = [
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "1 month" },
  { value: "90", label: "3 months" },
  { value: "180", label: "6 months" },
  { value: "365", label: "1 year" },
  { value: "custom", label: "Custom period" },
];

// Define the form schema
const extendSubscriptionSchema = z.object({
  extensionPeriod: z.string().min(1, "Please select an extension period"),
  customDays: z.number().optional().nullable(),
  chargeAdditional: z.boolean().default(false),
  additionalCharge: z
    .number()
    .min(0, "Additional charge cannot be negative")
    .optional()
    .nullable(),
  adminNotes: z.string().optional(),
});

type ExtendSubscriptionFormValues = z.infer<typeof extendSubscriptionSchema>;

interface ExtendSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    data: ExtendSubscriptionFormValues & { newEndDate: Date }
  ) => void;
  subscription: UserSubscription;
  isProcessing: boolean;
  userName: string;
}

export default function ExtendSubscriptionDialog({
  open,
  onOpenChange,
  onConfirm,
  subscription,
  isProcessing,
  userName,
}: ExtendSubscriptionDialogProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [newEndDate, setNewEndDate] = useState<Date | null>(null);

  // Initialize the form
  const form = useForm<ExtendSubscriptionFormValues>({
    resolver: zodResolver(extendSubscriptionSchema),
    defaultValues: {
      extensionPeriod: "",
      customDays: null,
      chargeAdditional: false,
      additionalCharge: null,
      adminNotes: `Extended subscription for ${userName}`,
    },
  });

  // Calculate the new end date based on the selected extension period
  const calculateNewEndDate = (period: string, customDays?: number | null) => {
    // Start from current end date or current date if no end date
    const startDate = subscription.subscriptionEnd
      ? new Date(subscription.subscriptionEnd)
      : new Date();

    if (period === "custom" && customDays) {
      return addDays(startDate, customDays);
    }

    const days = parseInt(period, 10);

    if (days <= 30) {
      return addDays(startDate, days);
    } else if (days <= 90) {
      return addMonths(startDate, 3);
    } else if (days <= 180) {
      return addMonths(startDate, 6);
    } else {
      return addYears(startDate, 1);
    }
  };

  // Calculate prorated additional cost based on days
  const calculateAdditionalCost = (days: number) => {
    if (!subscription.price) return 0;

    // Calculate daily rate based on original subscription
    const endDate = subscription.subscriptionEnd
      ? new Date(subscription.subscriptionEnd)
      : addDays(new Date(subscription.subscriptionStart), 30);
    const startDate = new Date(subscription.subscriptionStart);
    const subscriptionDays = Math.max(
      1,
      Math.round(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      )
    );

    const dailyRate = subscription.price / subscriptionDays;
    return Math.round(dailyRate * days * 100) / 100;
  };

  // Handle form submission
  const onSubmit = (data: ExtendSubscriptionFormValues) => {
    if (!newEndDate) {
      // Calculate end date if not already done
      const calculatedEndDate = calculateNewEndDate(
        data.extensionPeriod,
        data.customDays
      );

      onConfirm({
        ...data,
        newEndDate: calculatedEndDate,
      });
    } else {
      onConfirm({
        ...data,
        newEndDate,
      });
    }
  };

  // Handle period change
  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    form.setValue("extensionPeriod", value);

    // Set default additional charge if charging is enabled
    if (value !== "custom") {
      const days = parseInt(value, 10);
      const calculatedEndDate = calculateNewEndDate(value);
      setNewEndDate(calculatedEndDate);

      if (form.watch("chargeAdditional")) {
        form.setValue("additionalCharge", calculateAdditionalCost(days));
      }
    } else {
      setNewEndDate(null);
    }
  };

  // Handle custom days change
  const handleCustomDaysChange = (days: number) => {
    form.setValue("customDays", days);

    const calculatedEndDate = calculateNewEndDate("custom", days);
    setNewEndDate(calculatedEndDate);

    if (form.watch("chargeAdditional")) {
      form.setValue("additionalCharge", calculateAdditionalCost(days));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            Extend Subscription
          </DialogTitle>
          <DialogDescription>
            Extend {userName}'s subscription to{" "}
            {subscription?.subscriptionPlan?.name} by adding more time to the
            subscription period.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="extensionPeriod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Extension Period</FormLabel>
                  <Select
                    onValueChange={(value) => handlePeriodChange(value)}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select extension period" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {extensionPeriods.map((period) => (
                        <SelectItem key={period.value} value={period.value}>
                          {period.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedPeriod === "custom" && (
              <FormField
                control={form.control}
                name="customDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Days</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="1095" // Max 3 years
                        placeholder="Enter number of days"
                        {...field}
                        value={field.value?.toString() || ""}
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? null
                              : parseInt(e.target.value, 10);
                          field.onChange(value);
                          if (value) {
                            handleCustomDaysChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Enter the number of days to extend the subscription
                      (1-1095)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {newEndDate && (
              <div className="bg-muted/30 p-3 rounded-md">
                <div className="text-sm">
                  <span className="font-medium">Current end date: </span>
                  <span>
                    {subscription.subscriptionEnd
                      ? format(new Date(subscription.subscriptionEnd), "PPP")
                      : "No end date (perpetual)"}
                  </span>
                </div>
                <div className="text-sm mt-1">
                  <span className="font-medium">New end date: </span>
                  <span className="text-primary font-medium">
                    {format(newEndDate, "PPP")}
                  </span>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="chargeAdditional"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (checked) {
                          // If checked and we have a selected period, calculate default charge
                          if (selectedPeriod && selectedPeriod !== "custom") {
                            const days = parseInt(selectedPeriod, 10);
                            form.setValue(
                              "additionalCharge",
                              calculateAdditionalCost(days)
                            );
                          } else if (form.watch("customDays")) {
                            const days = form.watch("customDays") || 0;
                            form.setValue(
                              "additionalCharge",
                              calculateAdditionalCost(days)
                            );
                          }
                        } else {
                          form.setValue("additionalCharge", null);
                        }
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Charge for additional time</FormLabel>
                    <FormDescription>
                      Add a prorated charge for the extension period
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {form.watch("chargeAdditional") && (
              <FormField
                control={form.control}
                name="additionalCharge"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Additional Charge ({subscription.currencyCode})
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter additional charge"
                        {...field}
                        value={field.value?.toString() || ""}
                        onChange={(e) => {
                          field.onChange(
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value)
                          );
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Prorated cost based on current subscription
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="adminNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Admin Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter any notes about this extension"
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isProcessing || !newEndDate}>
                {isProcessing ? "Processing..." : "Extend Subscription"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
